import React from 'react';
import { createSurvey, updateSurvey } from './api';
import './App.css';
import './button.css';
import { TasksProvider, useTasks, useTasksDispatch } from './SurveyContext.js';
import TaskList from './TaskList.js';

function CreateSurvey({ goHome, survey }) {
  return (
    <TasksProvider initialTasks={survey?.questions ?? []}>
      <CreateSurveyContent goHome={goHome} survey={survey} />
    </TasksProvider>
  );
}

function CreateSurveyContent({ goHome, survey }) {
  const tasks = useTasks();
  const dispatch = useTasksDispatch();
  const [title, setTitle] = React.useState(survey?.title || '');
  const [previewMode, setPreviewMode] = React.useState(false);

  const handleAddQuestion = () => {
    const newId = tasks && tasks.length ? Math.max(...tasks.map(t => t.id)) + 1 : 0;
    dispatch({ type: 'added', id: newId, text: '', questionType: 'checkbox' });
  };

  const handleSave = async () => {
    const surveyData = {
      title: (title && title.trim()) ? title.trim() : 'Untitled Survey',
      questions: tasks
    };
    try {
      if (survey && survey.id) {
        const data = await updateSurvey(survey.id, surveyData);
        console.log('Survey updated:', data);
        alert('Survey updated successfully!');
        goHome();
        return;
      }

      const data = await createSurvey(surveyData);
      console.log('Survey saved:', data);
      alert('Survey saved successfully!');
      goHome();
    } catch (err) {
      console.error('Unexpected error saving survey:', err);
      alert('Error saving survey');
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-white to-blue-50">
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-blue-200 shadow-sm">
        <div className="flex items-center gap-4">
          <button className="px-3 py-2 rounded-md font-medium transition-all bg-blue-100 text-blue-700 hover:bg-blue-200 text-lg shadow-sm hover:shadow-md" onClick={previewMode ? () => setPreviewMode(false) : goHome}>←</button>
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-blue-900 m-0">{previewMode ? 'Preview Survey' : 'Create Survey'}</h2>
            {!previewMode && (
              <input
                placeholder="Enter survey title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="mt-2 px-3 py-2 border border-blue-300 rounded-md text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!previewMode && (
            <button className="px-4 py-2 rounded-md font-medium transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md" onClick={() => setPreviewMode(true)}>Preview</button>
          )}
          {previewMode && (
            <button className="px-4 py-2 rounded-md font-medium transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md" onClick={handleSave}>Confirm & Save</button>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        {!previewMode ? (
          <>
            <h3 className="text-lg font-bold text-blue-900 mb-4">Your Questions</h3>
            <TaskList handleAddQuestion={handleAddQuestion} />
          </>
        ) : (
          <SurveyPreview title={title} questions={tasks} />
        )}
      </div>
    </div>
  );
}

function SurveyPreview({ title, questions }) {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8 pb-6 border-b-2 border-blue-200">
        <h1 className="text-4xl font-bold text-blue-900 mb-2">{title || 'Untitled Survey'}</h1>
        <p className="text-blue-600">Review your survey before saving</p>
      </div>

      {questions.length === 0 ? (
        <div className="p-8 text-center bg-blue-50 rounded-lg border-2 border-dashed border-blue-300">
          <p className="text-blue-600 font-medium">No questions added yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {questions.map((q, idx) => (
            <div key={q.id} className="p-6 border border-blue-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Question {idx + 1}</div>
                  <h3 className="text-lg font-semibold text-blue-900 m-0">
                    {q.text || '(No question text)'}
                    {q.required && <span className="text-red-500 ml-2">*</span>}
                  </h3>
                </div>
              </div>

              {q.questionType === 'checkbox' && q.options?.length > 0 && (
                <div className="ml-4">
                  {q.options.map((opt, i) => (
                    <label key={i} className="flex items-center mb-3 text-sm cursor-pointer">
                      <input type="checkbox" disabled className="mr-3 w-4 h-4" />
                      <span className="text-blue-900">{opt}</span>
                    </label>
                  ))}
                </div>
              )}

              {q.questionType === 'radio' && q.options?.length > 0 && (
                <div className="ml-4">
                  {q.options.map((opt, i) => (
                    <label key={i} className="flex items-center mb-3 text-sm cursor-pointer">
                      <input type="radio" name={`preview-${q.id}`} disabled className="mr-3 w-4 h-4" />
                      <span className="text-blue-900">{opt}</span>
                    </label>
                  ))}
                </div>
              )}

              {q.questionType === 'text' && (
                <input type="text" placeholder="Short answer text" disabled className="w-full p-3 border border-blue-300 rounded-md text-sm bg-blue-50" />
              )}

              {q.questionType === 'textarea' && (
                <textarea placeholder="Long answer text" disabled className="w-full p-3 border border-blue-300 rounded-md text-sm min-h-24 bg-blue-50" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CreateSurvey;
